import { ZoomLevel } from "../types/zoom-levels";

/**
 * Utility functions for converting between Date and Temporal.PlainDate
 */

export const dateToPlainDate = (date: Date): Temporal.PlainDate => {
  return Temporal.PlainDate.from({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  });
};

export const plainDateToDate = (plainDate: Temporal.PlainDate): Date => {
  return new Date(plainDate.year, plainDate.month - 1, plainDate.day);
};

/**
 * Generate time key based on zoom level for efficient event lookups
 */
export function getTimeKey(date: Date, zoomLevel: ZoomLevel): string {
  switch (zoomLevel) {
    case "minute": {
      // Minute view shows minutes, so key by minute
      const hour = String(date.getHours()).padStart(2, "0");
      const minute = String(date.getMinutes()).padStart(2, "0");
      return `${date.toISOString().split("T")[0]}-${hour}-${minute}`; // YYYY-MM-DD-HH-MM
    }
    case "hourly": {
      // Hourly view shows hours, so key by hour
      const hourKey = String(date.getHours()).padStart(2, "0");
      return `${date.toISOString().split("T")[0]}-${hourKey}`; // YYYY-MM-DD-HH
    }
    case "daily":
      // Daily view shows individual days, so key by day
      return date.toISOString().split("T")[0]; // YYYY-MM-DD
    case "weekly": {
      // Weekly view shows weeks, so key by week (start of week)
      const startOfWeek = new Date(date);
      startOfWeek.setDate(date.getDate() - date.getDay()); // Sunday = 0
      return startOfWeek.toISOString().split("T")[0]; // YYYY-MM-DD of week start
    }
  }
}

/**
 * Calculates the appropriate date range based on zoom level and current range
 */
export function getAdjustedDateRange(
  currentRange: { start: Date; end: Date },
  zoomLevel: ZoomLevel,
): { start: Date; end: Date } {
  const currentStart = currentRange.start;
  const currentEnd = currentRange.end;
  const currentDurationMs = currentEnd.getTime() - currentStart.getTime();
  const currentCenterMs = currentStart.getTime() + currentDurationMs / 2;
  const currentCenter = new Date(currentCenterMs);

  switch (zoomLevel) {
    case "minute": {
      // Minute view: 1.5 hours total (1 hour selected + 30 min buffer each side)
      const totalMs = 90 * 60 * 1000; // 90 minutes in milliseconds
      const halfMs = totalMs / 2;

      return {
        start: new Date(currentCenter.getTime() - halfMs),
        end: new Date(currentCenter.getTime() + halfMs),
      };
    }
    case "hourly": {
      // Hourly view: 2 days total (1 day selected + 12 hour buffer each side)
      const totalMs = 2 * 24 * 60 * 60 * 1000; // 2 days in milliseconds
      const halfMs = totalMs / 2;

      return {
        start: new Date(currentCenter.getTime() - halfMs),
        end: new Date(currentCenter.getTime() + halfMs),
      };
    }
    case "daily": {
      // Daily view: max 30 days
      const maxDays = 30;
      const maxMs = maxDays * 24 * 60 * 60 * 1000;

      if (currentDurationMs <= maxMs) {
        return currentRange;
      }

      // Limit to 30 days centered on current center
      const halfMaxMs = maxMs / 2;
      return {
        start: new Date(currentCenter.getTime() - halfMaxMs),
        end: new Date(currentCenter.getTime() + halfMaxMs),
      };
    }
    case "weekly": {
      // Weekly view: max 1 year (52 weeks)
      const maxWeeks = 52;
      const maxMs = maxWeeks * 7 * 24 * 60 * 60 * 1000;

      if (currentDurationMs <= maxMs) {
        return currentRange;
      }

      // Limit to 1 year centered on current center
      const halfMaxMs = maxMs / 2;
      return {
        start: new Date(currentCenter.getTime() - halfMaxMs),
        end: new Date(currentCenter.getTime() + halfMaxMs),
      };
    }
  }
}
